// Content calendar: month/week views, day-detail slide-over, compose trigger.
//
// Timezone: post.at is epoch ms, bucketed and displayed in the user's chosen
// zone (the `timezone` prop; the browser's zone by default). Day bucketing lives
// in calendarUtils (dayKeyInTz); time-of-day display reads the zone from
// CalendarTzContext so the many small time cells don't each need it drilled in.
// See calendarUtils.ts for the bucketing rationale.
//
// TODO: drag-to-reschedule is intentionally out of scope for this pass.
// There is no reschedule API endpoint and dnd carries real risk on launch night.
// When the API is added, implement here using the existing api.schedule +
// api.unschedule pair (reschedule = unschedule old id, schedule new one).

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { formatInTz } from "../lib/tz";
import type {
  ConnectedAccount,
  DispatchRecord,
  PlatformId,
  PlatformInfo,
  ScheduledPost,
} from "../api";
import {
  heldReason,
  publishedStatuses,
  statusesFor,
  type PublishOutcome,
} from "../lib/platformStatus";
import {
  buildMonthGrid,
  buildWeekDays,
  groupCalendarItems,
  localDayKey,
  type CalendarDay,
  type CalendarItem,
  type PublishedItem,
  type ScheduledItem,
} from "../lib/calendarUtils";
import { humanizeDispatchError } from "../lib/errorMessage";
import { isRetryable } from "../lib/diagnose";
import { SlideOver } from "./SlideOver";
import { MetricsChips } from "./MetricsChips";
import { PlatformIcon } from "./PlatformIcon";

/** A compact row of real platform brand marks for a calendar item — replaces
 * the old bare colour dots so the "where" of a post reads at a glance. */
function ItemPlatformIcons({
  item,
  platforms,
  size = 13,
}: {
  item: CalendarItem;
  platforms: PlatformInfo[];
  size?: number;
}) {
  const ids =
    item.kind === "published"
      ? item.record.results.map((r) => r.platform)
      : (item.post.platforms as PlatformId[]);
  const infos = ids
    .map((id) => platforms.find((p) => p.id === id))
    .filter((p): p is PlatformInfo => Boolean(p));
  return (
    <span className="cal-post-icons">
      {infos.map((p) => (
        <PlatformIcon key={p.id} platform={p} size={size} />
      ))}
    </span>
  );
}

// How many posts to show per month cell before collapsing to "+N more".
// Capping at 3 keeps cells readable on typical screen sizes; overflows are
// always disclosed with an explicit "+N more" — never silently hidden.
const MAX_CELL_POSTS = 3;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DOW_SHORT = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const DOW_FULL  = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Badge CSS class per publish outcome — reuses the same semantics as Scheduled.tsx. */
const OUTCOME_CHIP: Record<PublishOutcome, string> = {
  will_publish: "sc-go",
  pending: "sc-pending",
  disconnected: "sc-off",
  unconfigured: "sc-off",
};

/** Dot CSS modifier per publish outcome. */
const OUTCOME_DOT: Record<PublishOutcome, string> = {
  will_publish: "go",
  pending: "pending",
  disconnected: "off",
  unconfigured: "off",
};

type ViewMode = "month" | "week";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// The active display timezone, provided once at the Calendar root so the many
// small time cells can read it without prop-drilling. Defaults to UTC only as a
// never-hit fallback (the provider always sets the real zone).
const CalendarTzContext = createContext<string>("UTC");

/** Time-of-day string in the given zone, shared by both detail cards. */
function timeOfDay(at: number, tz: string): string {
  return formatInTz(at, tz, { hour: "numeric", minute: "2-digit" });
}

/** Compact time for the dense week/day cards: "7:05 PM" → "7:05p" so it fits
 * beside the platform badges in a narrow column. 24-hour locales already return
 * a short "19:05" and pass through unchanged. */
function timeCompact(at: number, tz: string): string {
  return timeOfDay(at, tz)
    .replace(/\s*AM$/i, "a")
    .replace(/\s*PM$/i, "p");
}

/** Detail card for a SCHEDULED (upcoming) post: predicted outcome + Edit/Cancel.
 * The accent left-border and "Scheduled" pill mark it as not-yet-published — the
 * mirror of the green edge PublishedDetail wears. */
function ScheduledDetail({
  item,
  platforms,
  accounts,
  canceling,
  onCancel,
  onEdit,
}: {
  item: ScheduledItem;
  platforms: PlatformInfo[];
  accounts: ConnectedAccount[];
  canceling: boolean;
  onCancel: (id: string) => void;
  onEdit: (post: ScheduledPost) => void;
}) {
  const tz = useContext(CalendarTzContext);
  const statuses = statusesFor(item.post.platforms as PlatformId[], platforms, accounts);
  return (
    <div className="day-post day-post--scheduled">
      <div className="day-post-title">{item.title || "Untitled post"}</div>
      <div className="day-post-time muted small">
        <span className="sc-pill">Scheduled</span> {timeOfDay(item.at, tz)}
      </div>
      <div className="chips">
        {statuses.map((s) => (
          <span
            key={s.id}
            className={`chip ${OUTCOME_CHIP[s.outcome]}`}
            title={s.note ?? `${s.name} will publish`}
          >
            {s.outcome === "will_publish" ? "✓" : s.outcome === "pending" ? "⏳" : "✕"}{" "}
            {s.name}
          </span>
        ))}
      </div>
      <div className="day-post-actions">
        <button
          type="button"
          className="ghost small"
          disabled={canceling}
          onClick={() => onEdit(item.post)}
        >
          Edit
        </button>
        <button
          type="button"
          className="ghost small"
          disabled={canceling}
          onClick={() => onCancel(item.id)}
        >
          {canceling ? "Canceling…" : "Cancel"}
        </button>
      </div>
    </div>
  );
}

/**
 * Detail card for a PUBLISHED post: the ACTUAL result of each leg. A published
 * leg shows a live link to the real post (when the platform returned a url); a
 * failed leg shows its error text. There is no Cancel — the post already fired,
 * so cancelling makes no sense.
 */
/**
 * The done marker for a published post. Green ✓ only when at least one platform
 * actually published; red ✗ when every leg failed — a fully-failed post must
 * not wear a success check. (A partial success still reads as ✓; the red
 * platform badge carries the failed-leg detail.)
 */
function DoneMarker({ item }: { item: PublishedItem }) {
  const anyPublished = item.record.results.some((r) => r.ok);
  return (
    <span className={`cal-done${anyPublished ? "" : " cal-done--failed"}`} aria-hidden="true">
      {anyPublished ? "✓" : "✕"}
    </span>
  );
}

const OUTCOME_WORD = { published: "Published", held: "Held", failed: "Failed" } as const;

function PublishedDetail({
  item,
  platforms,
  onClearPost,
  onRepost,
  onRetry,
}: {
  item: PublishedItem;
  platforms: PlatformInfo[];
  /** Hide this published post from the calendar (it stays in Analytics). */
  onClearPost?: (id: string) => void;
  /** Re-post a failed post (reopens the composer with its draft). */
  onRepost?: (rec: DispatchRecord) => void;
  /** Retry a failed post's transient legs immediately (no composer). */
  onRetry?: (rec: DispatchRecord) => void;
}) {
  const tz = useContext(CalendarTzContext);
  const statuses = publishedStatuses(item.record.results, platforms);
  const byId = new Map(platforms.map((p) => [p.id, p]));
  const failedLegs = statuses.filter((s) => s.outcome === "failed");
  const hasDraft = item.record.draft != null;
  const allRetryable =
    failedLegs.length > 0 && failedLegs.every((s) => s.error != null && isRetryable(s.error));
  const showRetry = hasDraft && allRetryable && onRetry != null;
  const showRepost = hasDraft && failedLegs.length > 0 && !showRetry && onRepost != null;
  return (
    <div className="day-post day-post--published">
      <div className="day-post-headrow">
        <div className="day-post-title">
          <DoneMarker item={item} /> {item.title || "Untitled post"}
        </div>
        <span className="day-post-actions">
          {showRetry && (
            <button
              type="button"
              className="btn small"
              title="Send this post's failed platforms again"
              onClick={() => onRetry(item.record)}
            >
              Retry
            </button>
          )}
          {showRepost && (
            <button
              type="button"
              className="btn small"
              title="Reopen this post in the composer to fix and send it again"
              onClick={() => onRepost(item.record)}
            >
              Re-post →
            </button>
          )}
          {onClearPost && (
            <button
              type="button"
              className="day-post-clear ghost small"
              title="Hide this post from the calendar (it stays in Analytics)"
              onClick={() => onClearPost(item.id)}
            >
              Clear
            </button>
          )}
        </span>
      </div>
      <div className="day-post-time muted small">Published {timeOfDay(item.at, tz)}</div>

      {/* Delivery receipt: one row per platform with an explicit status WORD
          (never colour alone), a link to the live post, and — for a held or
          failed leg — a plain-English line of what happened. */}
      <ul className="day-receipt">
        {statuses.map((s) => {
          const p = byId.get(s.id);
          return (
            <li key={s.id} className={`day-receipt-leg is-${s.outcome}`}>
              <span className="day-receipt-lead">
                {p && <PlatformIcon platform={p} size={16} />}
                <span className="day-receipt-name">{s.name}</span>
              </span>
              <span className="day-receipt-status">{OUTCOME_WORD[s.outcome]}</span>
              {s.outcome === "published" && (
                <MetricsChips metrics={item.record.metrics?.[s.id]} />
              )}
              {s.url && (
                <a
                  className="day-receipt-link"
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`View the ${s.name} post`}
                >
                  View post ↗
                </a>
              )}
              {s.outcome !== "published" && s.error && (
                <span className="day-receipt-note">
                  {s.outcome === "held" ? heldReason(s.id) : humanizeDispatchError(s.error)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The list of items (scheduled + published) shown in the day-detail slide-over. */
function DayItemList({
  items,
  platforms,
  accounts,
  onCancel,
  onEdit,
  onClearPost,
  onRepost,
  onRetry,
}: {
  items: CalendarItem[];
  platforms: PlatformInfo[];
  accounts: ConnectedAccount[];
  onCancel: (id: string) => Promise<void>;
  onEdit: (post: ScheduledPost) => void;
  onClearPost?: (id: string) => void;
  onRepost?: (rec: DispatchRecord) => void;
  onRetry?: (rec: DispatchRecord) => void;
}) {
  const [canceling, setCanceling] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleCancel(id: string) {
    setCanceling(id);
    setError(null);
    try {
      await onCancel(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not cancel.");
    } finally {
      setCanceling(null);
    }
  }

  if (items.length === 0) {
    return <p className="muted small">No posts for this day.</p>;
  }

  return (
    <div className="day-posts">
      {items.map((item) =>
        item.kind === "published" ? (
          <PublishedDetail
            key={item.id}
            item={item}
            platforms={platforms}
            onClearPost={onClearPost}
            onRepost={onRepost}
            onRetry={onRetry}
          />
        ) : (
          <ScheduledDetail
            key={item.id}
            item={item}
            platforms={platforms}
            accounts={accounts}
            canceling={canceling === item.id}
            onCancel={(id) => void handleCancel(id)}
            onEdit={onEdit}
          />
        ),
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/** Month grid: 4–6 rows of 7 cells, Sunday–Saturday. */
function MonthGrid({
  grid,
  itemsByDay,
  platforms,
  onDayClick,
}: {
  grid: ReturnType<typeof buildMonthGrid>;
  itemsByDay: Map<string, CalendarItem[]>;
  platforms: PlatformInfo[];
  onDayClick: (day: CalendarDay) => void;
}) {
  return (
    <div className="cal-month">
      {/* Day-of-week header row */}
      <div className="cal-dow-row" aria-hidden="true">
        {DOW_SHORT.map((d) => (
          <div key={d} className="cal-dow">
            {d}
          </div>
        ))}
      </div>

      {grid.map((week, wi) => (
        <div key={wi} className="cal-week-row">
          {week.map((day, di) => {
            const key = localDayKey(day.date);
            const items = itemsByDay.get(key) ?? [];
            const visible = items.slice(0, MAX_CELL_POSTS);
            const overflow = items.length - visible.length;

            return (
              <div
                key={di}
                className={[
                  "cal-cell",
                  day.isToday ? "cal-cell--today" : "",
                  !day.isCurrentMonth ? "cal-cell--other" : "",
                  items.length > 0 ? "cal-cell--has-posts" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                role="button"
                tabIndex={0}
                aria-label={`${day.date.toLocaleDateString(undefined, {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}${items.length > 0 ? `, ${items.length} post${items.length > 1 ? "s" : ""}` : ", no posts — click to compose"}`}
                onClick={() => onDayClick(day)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onDayClick(day);
                  }
                }}
              >
                {/* Day number — styled as a filled circle when today */}
                <span className={`cal-day-num${day.isToday ? " cal-day-num--today" : ""}`}>
                  {day.date.getDate()}
                </span>

                {/* Post previews — published items carry a ✓ done marker so
                    the past reads as complete, not pending. */}
                {visible.length > 0 && (
                  <div className="cal-cell-posts">
                    {visible.map((item) => (
                      <div
                        key={item.id}
                        className={`cal-post-row${
                          item.kind === "published"
                            ? item.record.results.some((r) => r.ok)
                              ? " cal-post-row--done"
                              : " cal-post-row--done cal-post-row--failed"
                            : " cal-post-row--scheduled"
                        }`}
                      >
                        {item.kind === "published" ? (
                          <DoneMarker item={item} />
                        ) : (
                          // Accent clock marks an upcoming post — cancellable/editable,
                          // unlike the green ✓ past.
                          <span className="cal-sched-mark" aria-hidden="true">
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="9" />
                              <polyline points="12 7 12 12 15 14" />
                            </svg>
                          </span>
                        )}
                        <span className="cal-post-title">{item.title || "Untitled"}</span>
                        <ItemPlatformIcons item={item} platforms={platforms} />
                      </div>
                    ))}
                    {overflow > 0 && (
                      <div className="cal-overflow">+{overflow} more</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/**
 * One post inside a week cell. PUBLISHED items get a ✓ + result-coloured
 * platform badges (green published / red failed); SCHEDULED items keep the
 * predicted-outcome badges. Both render side by side on the same day.
 */
function WeekPost({
  item,
  platforms,
  accounts,
}: {
  item: CalendarItem;
  platforms: PlatformInfo[];
  accounts: ConnectedAccount[];
}) {
  const tz = useContext(CalendarTzContext);
  const byId = new Map(platforms.map((p) => [p.id, p]));

  if (item.kind === "published") {
    const statuses = publishedStatuses(item.record.results, platforms);
    const anyOk = item.record.results.some((r) => r.ok);
    const anyFail = statuses.some((s) => s.outcome === "failed");
    const stateClass = anyFail
      ? " cal-week-post--failed"
      : !anyOk
        ? " cal-week-post--held"
        : " cal-week-post--done";
    return (
      <div className={`cal-week-post${stateClass}`}>
        <div className="cal-week-post-main">
          <div className="cal-week-post-title">{item.title || "Untitled"}</div>
          <div className="cal-week-post-platforms">
            {statuses.map((s) => {
              const p = byId.get(s.id);
              return (
                <span
                  key={s.id}
                  className={`cal-plat cal-plat--${s.outcome}`}
                  title={
                    s.outcome === "published"
                      ? `${s.name} published`
                      : s.outcome === "held"
                        ? `${s.name} — not publishing yet`
                        : `${s.name} failed${s.error ? `: ${humanizeDispatchError(s.error)}` : ""}`
                  }
                >
                  {p ? <PlatformIcon platform={p} size={16} /> : s.name}
                </span>
              );
            })}
          </div>
          <time className="cal-week-post-time">{timeCompact(item.at, tz)}</time>
        </div>
      </div>
    );
  }

  const statuses = statusesFor(item.post.platforms as PlatformId[], platforms, accounts);
  return (
    <div className="cal-week-post cal-week-post--scheduled">
      <div className="cal-week-post-main">
        <div className="cal-week-post-eyebrow">Scheduled</div>
        <div className="cal-week-post-title">{item.title || "Untitled"}</div>
        <div className="cal-week-post-platforms">
          {statuses.map((s) => {
            const p = byId.get(s.id);
            return (
              <span
                key={s.id}
                className={`cal-plat cal-plat--${OUTCOME_DOT[s.outcome]}`}
                title={s.note ?? `${s.name} will publish`}
              >
                {p ? <PlatformIcon platform={p} size={16} /> : s.name}
              </span>
            );
          })}
        </div>
        <time className="cal-week-post-time">{timeCompact(item.at, tz)}</time>
      </div>
    </div>
  );
}

/** Week view: 7 taller cells with room for time + platform names. */
function WeekView({
  days,
  itemsByDay,
  platforms,
  accounts,
  onDayClick,
}: {
  days: CalendarDay[];
  itemsByDay: Map<string, CalendarItem[]>;
  platforms: PlatformInfo[];
  accounts: ConnectedAccount[];
  onDayClick: (day: CalendarDay) => void;
}) {
  return (
    <div className="cal-week-view">
      {days.map((day, di) => {
        const key = localDayKey(day.date);
        const items = itemsByDay.get(key) ?? [];

        return (
          <div
            key={di}
            className={[
              "cal-week-cell",
              day.isToday ? "cal-week-cell--today" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            role="button"
            tabIndex={0}
            aria-label={`${day.date.toLocaleDateString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}${items.length > 0 ? `, ${items.length} post${items.length > 1 ? "s" : ""}` : ", no posts — click to compose"}`}
            onClick={() => onDayClick(day)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onDayClick(day);
              }
            }}
          >
            <div className="cal-week-cell-header">
              <span className="cal-week-dow">{DOW_FULL[day.date.getDay()]}</span>
              <span className={`cal-week-date${day.isToday ? " cal-week-date--today" : ""}`}>
                {day.date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            </div>

            <div className="cal-week-posts">
              {items.length === 0 ? (
                <div className="cal-week-empty muted small">No posts</div>
              ) : (
                <>
                  {items.slice(0, MAX_CELL_POSTS).map((item) => (
                    <WeekPost key={item.id} item={item} platforms={platforms} accounts={accounts} />
                  ))}
                  {items.length > MAX_CELL_POSTS && (
                    // Clicking anywhere in the cell (this included) opens the
                    // day-detail popup listing every post for the day.
                    <div className="cal-overflow cal-week-overflow">
                      +{items.length - MAX_CELL_POSTS} more
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Calendar component
// ---------------------------------------------------------------------------

export function Calendar({
  scheduled,
  history,
  platforms,
  accounts,
  onCancelScheduled,
  onEdit,
  onRepost,
  onRetry,
  onComposeRequest,
  focusItem,
  defaultView = "week",
  onClearPost,
  clearedCount = 0,
  onRestoreCleared,
  timezone,
}: {
  scheduled: ScheduledPost[];
  /** Published dispatch records — folded in so past days aren't blank. */
  history: DispatchRecord[];
  platforms: PlatformInfo[];
  accounts: ConnectedAccount[];
  /** Cancel a scheduled post — the app's single confirm-guarded handler, which
   * also reloads data once the delete succeeds. */
  onCancelScheduled: (id: string) => Promise<void>;
  /** Reopen a scheduled post in the composer to reschedule/edit it. */
  onEdit: (post: ScheduledPost) => void;
  /** Re-post a failed dispatch (reopens the composer with its draft). */
  onRepost?: (rec: DispatchRecord) => void;
  /** Retry a failed dispatch's transient legs immediately (no composer). */
  onRetry?: (rec: DispatchRecord) => void;
  /** Called when an empty day is clicked — lets the parent open the compose panel. */
  onComposeRequest: () => void;
  /** External request to reveal a specific item (e.g. from the Needs-attention
   * rail): moves the month view to that date and opens its day-detail so the
   * post's real per-leg result is shown. A fresh object each time re-triggers,
   * so the same item can be re-opened after closing. */
  focusItem?: { at: number; id: string } | null;
  /** Which view to open in. The Dashboard opens on "week" ("what's going out
   * now"); the dedicated Calendar page opens on the full "month" grid. */
  defaultView?: ViewMode;
  /** Hide a published post from the calendar (stays in Analytics). */
  onClearPost?: (id: string) => void;
  /** How many posts are currently hidden — drives the "Restore" footer. */
  clearedCount?: number;
  /** Un-hide every cleared post. */
  onRestoreCleared?: () => void;
  /** IANA zone posts are bucketed and displayed in (browser zone by default). */
  timezone: string;
}) {
  // Week is the Dashboard default: its job is "what's going out now", which a
  // week answers far better than a mostly-empty month grid. The Calendar page
  // overrides this to "month".
  const [view, setView] = useState<ViewMode>(defaultView);

  // Month view navigation state.
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());

  // Week view navigation anchor — any date within the desired week.
  const [weekAnchor, setWeekAnchor] = useState(() => new Date());

  // Day-detail slide-over: store only the date; posts are derived from live data.
  const [detailDate, setDetailDate] = useState<Date | null>(null);

  // Today — read once per render (updates when user navigates the calendar or
  // data refreshes; midnight crossover is an acceptable miss until next render).
  const today = new Date();

  // Fold both sources — upcoming (scheduled) and already-published (history) —
  // into one day-keyed Map so a day that already fired isn't blank.
  const itemsByDay = useMemo(
    () => groupCalendarItems(scheduled, history, timezone),
    [scheduled, history, timezone],
  );

  // Month grid — recomputed only when year/month (or the zone that decides
  // "today") changes.
  const monthGrid = useMemo(
    () => buildMonthGrid(calYear, calMonth, today, timezone),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calYear, calMonth, timezone],
  );

  // Week days — recomputed only when the anchor (or the zone) changes.
  const weekDays = useMemo(
    () => buildWeekDays(weekAnchor, today, timezone),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekAnchor, timezone],
  );

  // Items for the currently open day-detail slide-over — derived from live data
  // so cancellations update the list automatically without closing/reopening.
  const detailItems = detailDate ? (itemsByDay.get(localDayKey(detailDate)) ?? []) : [];

  // Auto-close the day detail once all its items are gone (e.g. the only
  // scheduled post was cancelled). A day with published items never empties.
  useEffect(() => {
    if (detailDate && detailItems.length === 0) {
      setDetailDate(null);
    }
  }, [detailDate, detailItems.length]);

  // Reveal an externally-requested item: switch to the month it lives in and
  // open that day's detail (which renders the published record's per-leg
  // errors). A new focusItem object is supplied per request, so the effect
  // re-fires even when the same failure is re-opened after closing.
  useEffect(() => {
    if (!focusItem) return;
    const d = new Date(focusItem.at);
    setView("month");
    setCalYear(d.getFullYear());
    setCalMonth(d.getMonth());
    setDetailDate(d);
  }, [focusItem]);

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  function prevPeriod() {
    if (view === "month") {
      if (calMonth === 0) {
        setCalYear((y) => y - 1);
        setCalMonth(11);
      } else {
        setCalMonth((m) => m - 1);
      }
    } else {
      setWeekAnchor(
        (a) => new Date(a.getFullYear(), a.getMonth(), a.getDate() - 7),
      );
    }
  }

  function nextPeriod() {
    if (view === "month") {
      if (calMonth === 11) {
        setCalYear((y) => y + 1);
        setCalMonth(0);
      } else {
        setCalMonth((m) => m + 1);
      }
    } else {
      setWeekAnchor(
        (a) => new Date(a.getFullYear(), a.getMonth(), a.getDate() + 7),
      );
    }
  }

  // ---------------------------------------------------------------------------
  // Day-click handler
  // ---------------------------------------------------------------------------

  function onDayClick(day: CalendarDay) {
    const key = localDayKey(day.date);
    const items = itemsByDay.get(key) ?? [];
    if (items.length > 0) {
      // Show the item list for this day (scheduled and/or published).
      setDetailDate(day.date);
    } else {
      // Empty day → open compose panel. The date is not pre-filled in Composer
      // (Composer has no initialDate prop — see brief). The user sets the
      // schedule time manually inside the composer form.
      onComposeRequest();
    }
  }

  // ---------------------------------------------------------------------------
  // Header text
  // ---------------------------------------------------------------------------

  const headerText =
    view === "month"
      ? `${MONTH_NAMES[calMonth]} ${calYear}`
      : `Week of ${weekDays[0]!.date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        })}`;

  const detailTitle = detailDate
    ? detailDate.toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : "";

  const totalScheduled = scheduled.length;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <CalendarTzContext.Provider value={timezone}>
      <div className="cal">
        {/* Calendar header: navigation + count + view toggle */}
        <div className="cal-header">
          <div className="cal-nav">
            <button
              type="button"
              className="ghost small cal-nav-btn"
              onClick={prevPeriod}
              aria-label={view === "month" ? "Previous month" : "Previous week"}
            >
              ‹
            </button>
            <span className="cal-period">{headerText}</span>
            <button
              type="button"
              className="ghost small cal-nav-btn"
              onClick={nextPeriod}
              aria-label={view === "month" ? "Next month" : "Next week"}
            >
              ›
            </button>
            {totalScheduled > 0 && (
              <span className="cal-count muted small">
                {totalScheduled} post{totalScheduled !== 1 ? "s" : ""} scheduled
              </span>
            )}
          </div>

          <div className="cal-view-toggle">
            <button
              type="button"
              className={`btn small${view === "month" ? " primary" : ""}`}
              onClick={() => setView("month")}
              aria-pressed={view === "month"}
            >
              Month
            </button>
            <button
              type="button"
              className={`btn small${view === "week" ? " primary" : ""}`}
              onClick={() => setView("week")}
              aria-pressed={view === "week"}
            >
              Week
            </button>
          </div>
        </div>

        {view === "month" ? (
          <MonthGrid
            grid={monthGrid}
            itemsByDay={itemsByDay}
            platforms={platforms}
            onDayClick={onDayClick}
          />
        ) : (
          <WeekView
            days={weekDays}
            itemsByDay={itemsByDay}
            platforms={platforms}
            accounts={accounts}
            onDayClick={onDayClick}
          />
        )}
        {clearedCount > 0 && onRestoreCleared && (
          <div className="cal-hidden-note">
            <span className="muted small">
              {clearedCount} post{clearedCount !== 1 ? "s" : ""} hidden from this calendar
            </span>
            <button type="button" className="link-btn" onClick={onRestoreCleared}>
              Restore
            </button>
          </div>
        )}
      </div>

      {/* Day-detail slide-over: scheduled (cancellable) + published (linked) */}
      {detailDate && (
        <SlideOver
          open={true}
          title={detailTitle}
          onClose={() => setDetailDate(null)}
        >
          <DayItemList
            items={detailItems}
            platforms={platforms}
            accounts={accounts}
            onCancel={onCancelScheduled}
            onEdit={onEdit}
            onClearPost={onClearPost}
            onRepost={onRepost}
            onRetry={onRetry}
          />
        </SlideOver>
      )}
    </CalendarTzContext.Provider>
  );
}
